import { CSSProperties, PropsWithChildren } from 'react'
import { Column, Heading, SplitRow } from '@skedulo/breeze-ui-react'
import { hasPermission } from '../../core/globals'
import { ErrorLevel, PagesPermission, UIErrorCode } from '../constants'
import { ReactComponent as RoadblockErrorSVG } from '../images/roadblock-error.svg'
import { ReactComponent as TemplateErrorSVG } from '../images/template-error.svg'
import { getErrorCTA, getErrorMessage } from '../utils'
import { DebuggableErrorFallback } from './debuggable-error-fallback'
import { ErrorDetail } from './error-detail'
import type { ErrorFallbackProps } from './error-fallback'
import type { FullscreenErrorLevel } from '../types'

const getFullscreenErrorHeadingText = (level?: FullscreenErrorLevel) => {
  switch (level) {
    case ErrorLevel.TEMPLATE:
      return <>There’s a problem loading this&nbsp;page</>

    case ErrorLevel.HOST:
      return <>We are working on it.</>

    default:
      return null
  }
}

const getFullscreenErrorSVG = (level?: FullscreenErrorLevel) => {
  switch (level) {
    case ErrorLevel.TEMPLATE:
      return <TemplateErrorSVG />

    case ErrorLevel.HOST:
      return <RoadblockErrorSVG />

    default:
      return null
  }
}

export type FullscreenErrorFallbackProps =
  ErrorFallbackProps<FullscreenErrorLevel>

/**
 * Renders a fullscreen layout to use as a fallback UI when too much of the
 * application has broken for it to be usable. Accommodates a navbar, and takes
 * over remaining screen space.
 */
export const FullscreenErrorFallback = ({
  error,
  reset,
  code = UIErrorCode.UNKNOWN,
  level = ErrorLevel.TEMPLATE,
  traceId,
  supportEmail,
  children,
}: PropsWithChildren<FullscreenErrorFallbackProps>) => {
  const hasEnhancedErrorsPermission = hasPermission(
    PagesPermission.ENHANCED_ERRORS,
  )
  const isTemplateError = level === ErrorLevel.TEMPLATE

  return (
    <Column
      align="center"
      style={
        {
          '--top-offset':
            'var(--navbar-height, calc(var(--brz-spacing-12) + var(--brz-spacing-1)))',
          backgroundColor: 'var(--brz-color-white)',
          position: 'fixed',
          top: 'var(--top-offset)',
          left: 0,
          height: 'calc(100vh - var(--top-offset))',
          width: '100vw',
          paddingTop: !hasEnhancedErrorsPermission && '10rem',
          textAlign: 'initial', // Workaround for bug caused by `align` attribute clash (browser behaviour)
          zIndex: 'var(--brz-z-index-dialog)',
        } as CSSProperties
      }
    >
      {isTemplateError && hasEnhancedErrorsPermission ? (
        <DebuggableErrorFallback
          code={code}
          traceId={traceId}
          error={error}
          reset={reset}
          level={level}
        />
      ) : (
        <SplitRow
          leftExpand
          style={{
            '--brz-row-spacing': 'var(--brz-spacing-12)',
            maxWidth: '920px',
          }}
        >
          <div
            slot="left"
            style={{
              fontSize: 'var(--brz-font-size-lg)',
              lineHeight: 'var(--brz-line-height-lg)',
            }}
          >
            <Heading
              size="3xl"
              style={{
                marginBottom: 'var(--brz-spacing-5)',
                marginTop: 'var(--brz-spacing-8)',
              }}
            >
              {getFullscreenErrorHeadingText(level)}
            </Heading>

            <ErrorDetail code={code} traceId={traceId}>
              {children || getErrorMessage(level)}
            </ErrorDetail>

            <div style={{ marginTop: 'var(--brz-spacing-8)' }}>
              {getErrorCTA(level, code, traceId, supportEmail)}
            </div>
          </div>

          <div slot="right">{getFullscreenErrorSVG(level)}</div>
        </SplitRow>
      )}
    </Column>
  )
}
